import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ROOM_CODE_ALPHABET } from '@ringmaster/shared';
import { useRoom } from '../net/useRoom.js';
import { Glyph } from '../ui/Glyph.js';

/**
 * JoinScreen — code input + name input + big primary pill "Join".
 * - 4-char code, auto-uppercase, autofocus
 * - Pre-fills from ?code= URL param
 * - Errors shown inline (bad code, name taken, game started)
 */

// Derive the allowed-character filter straight from the shared alphabet so it
// can never drift from the server's room-code minting (previously a hand-typed
// copy silently dropped 'L' and rejected valid codes).
const DISALLOWED_CHARS = new RegExp(`[^${ROOM_CODE_ALPHABET}]`, 'g');

function sanitizeCode(raw: string): string {
  return raw.toUpperCase().replace(DISALLOWED_CHARS, '').slice(0, 4);
}

export function JoinScreen() {
  const { send, lastError } = useRoom();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const codeRef = useRef<HTMLInputElement>(null);

  // Pre-fill from ?code= URL param.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const param = params.get('code');
    if (param) {
      setCode(sanitizeCode(param));
      // Focus name field if code is pre-filled.
      setTimeout(() => {
        const nameEl = document.getElementById('hand-name-input');
        nameEl?.focus();
      }, 50);
    } else {
      codeRef.current?.focus();
    }
  }, []);

  function handleCodeChange(e: React.ChangeEvent<HTMLInputElement>) {
    setCode(sanitizeCode(e.target.value));
  }

  function handleJoin() {
    const trimmedName = name.trim();
    if (code.length !== 4 || !trimmedName) return;
    send({ type: 'JOIN_ROOM', code, name: trimmedName });
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') handleJoin();
  }

  const canJoin = code.length === 4 && name.trim().length > 0;

  // Map error codes to human-friendly messages.
  const errorMsg = lastError
    ? lastError.code === 'ROOM_NOT_FOUND'
      ? 'Room not found — check the code.'
      : lastError.code === 'NAME_TAKEN'
        ? 'That name is already taken — try another.'
        : lastError.code === 'GAME_ALREADY_STARTED'
          ? 'That game has already started.'
          : lastError.code === 'INVALID_CODE'
            ? 'Invalid code — must be 4 letters.'
            : lastError.code === 'ROOM_FULL'
              ? 'The room is full.'
              : lastError.code === 'KICKED'
                ? 'The host removed you from the lobby — rejoin below.'
                : lastError.code === 'SESSION_INVALID'
                  ? 'Your session expired — rejoin below.'
                  : lastError.message
    : null;

  return (
    <div
      className="game-screen"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(180deg, #060406 0%, #1a0d05 100%)',
        padding: '0 var(--space-5)',
        gap: 'var(--space-6)',
      }}
    >
      <style>{`
        .rm-join-input::placeholder { color: rgba(255,255,255,0.22); opacity: 1; }
        .rm-join-input:focus { border-color: var(--gold-4, #c9923f); box-shadow: 0 0 0 2px rgba(201,146,63,0.5); }
      `}</style>

      <Link
        to="/"
        aria-label="Back to home"
        style={{
          position: 'fixed',
          top: 'max(16px, env(safe-area-inset-top))',
          left: 'max(16px, env(safe-area-inset-left))',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: 'var(--radius-pill)',
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.09)',
          color: 'var(--text-primary)',
          textDecoration: 'none',
        }}
      >
        <ArrowLeft size={20} strokeWidth={2} />
      </Link>

      {/* Title */}
      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-3)' }}>
        <Glyph name="topHat" size={40} tone="gold" />
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, margin: 0, letterSpacing: '0.03em' }}>
          Join the table
        </h1>
        <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: 14 }}>
          Enter the room code from your host
        </p>
      </div>

      {/* Form card */}
      <div
        style={{
          width: '100%',
          maxWidth: 360,
          background: 'linear-gradient(180deg, var(--ink-2, #150d0f) 0%, var(--ink-1, #0b0709) 100%)',
          border: '1px solid var(--gold-2, #7a4c1e)',
          borderRadius: 'var(--radius-card)',
          padding: 'var(--space-6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-5)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        {/* Code field */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <label
            htmlFor="hand-code-input"
            style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em' }}
          >
            Room code
          </label>
          <input
            id="hand-code-input"
            className="rm-join-input"
            ref={codeRef}
            type="text"
            inputMode="text"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="characters"
            spellCheck={false}
            placeholder="RVNK"
            value={code}
            onChange={handleCodeChange}
            onKeyDown={handleKeyDown}
            maxLength={4}
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 32,
              letterSpacing: '0.25em',
              textAlign: 'center',
              textTransform: 'uppercase',
              background: 'rgba(255,255,255,0.06)',
              border: `1px solid ${lastError?.code === 'ROOM_NOT_FOUND' || lastError?.code === 'INVALID_CODE' ? 'var(--accent-blood-end)' : 'var(--surface-stroke)'}`,
              borderRadius: 12,
              color: 'var(--text-primary)',
              padding: 'var(--space-4)',
              width: '100%',
              boxSizing: 'border-box',
              outline: 'none',
              caretColor: 'var(--text-primary)',
            }}
          />
        </div>

        {/* Name field */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <label
            htmlFor="hand-name-input"
            style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em' }}
          >
            Your name
          </label>
          <input
            id="hand-name-input"
            className="rm-join-input"
            type="text"
            autoComplete="name"
            autoCapitalize="words"
            placeholder="Cara"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 20))}
            onKeyDown={handleKeyDown}
            maxLength={20}
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 18,
              background: 'rgba(255,255,255,0.06)',
              border: `1px solid ${lastError?.code === 'NAME_TAKEN' ? 'var(--accent-blood-end)' : 'var(--surface-stroke)'}`,
              borderRadius: 12,
              color: 'var(--text-primary)',
              padding: 'var(--space-4)',
              width: '100%',
              boxSizing: 'border-box',
              outline: 'none',
              caretColor: 'var(--text-primary)',
            }}
          />
        </div>

        {/* Error */}
        {errorMsg ? (
          <p
            role="alert"
            style={{
              color: 'var(--accent-blood-end)',
              fontSize: 13,
              margin: 0,
              textAlign: 'center',
            }}
          >
            {errorMsg}
          </p>
        ) : null}

        {/* Join button */}
        <div style={{ marginTop: 'var(--space-2)' }}>
          <button
            type="button"
            disabled={!canJoin}
            onClick={handleJoin}
            style={{
              width: '100%',
              fontFamily: 'var(--font-ui)',
              fontWeight: 600,
              fontSize: 16,
              borderRadius: 'var(--radius-pill)',
              padding: '16px 24px',
              background: canJoin ? 'var(--btn-primary-bg)' : 'rgba(255,255,255,0.2)',
              color: canJoin ? 'var(--btn-primary-text)' : 'rgba(255,255,255,0.5)',
              border: 'none',
              cursor: canJoin ? 'pointer' : 'not-allowed',
              boxShadow: canJoin ? 'var(--rim-gold, 0 0 24px rgba(230,184,102,0.5))' : 'none',
              transition: 'background var(--duration-fast) var(--ease-standard), box-shadow var(--duration-base) var(--ease-standard)',
              minHeight: 52,
            }}
          >
            Join game
          </button>
        </div>
      </div>
    </div>
  );
}
